import * as yup from "yup";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Slide,
  Switch,
  Box,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import { roleInProject, skillsUsedInProjects } from "config";
import ProjectDisplay from "components/ProjectDisplay";
import {
  useAddNewProjectMutation,
  useDeleteCvProjectMutation,
  useEditCvProjectMutation,
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Select from "react-select";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";

const setMonthToNumber = (month) => {
  switch (month) {
    case "January":
      return 1;
    case "February":
      return 2;
    case "March":
      return 3;
    case "April":
      return 4;
    case "May":
      return 5;
    case "June":
      return 6;
    case "July":
      return 7;
    case "August":
      return 8;
    case "September":
      return 9;
    case "October":
      return 10;
    case "November":
      return 11;
    case "December":
      return 12;

    default:
      return "neutral";
  }
};

const projRegex = /^https:\/\/[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,}){1,}([\/\S]*)?$/;

const projectSchema = yup.object().shape({
  name: yup.string().required("Project name is required"),
  onGoing: yup.boolean(),
  startMonth: yup.string().required("Month started is required"),
  startYear: yup.string().required("Year started is required"),
  endYear: yup.string().when("onGoing", {
    is: false,
    then: yup
      .string()
      .test("endYear", "End year is required year", function (value, context) {
        var endYear = new Date(value);
        var specificEndYear = endYear.getFullYear();
        var specificStartYear = new Date(
          context.parent.startYear
        ).getFullYear();

        return specificEndYear >= specificStartYear;
      }),
    notRequired: yup.boolean(),
  }),

  endMonth: yup.string().when(["onGoing", "startYear", "endYear"], {
    is: (onGoing) => !onGoing,
    then: yup
      .string()
      .test(
        "endMonth",
        "End month for the same year cannot be before start month",
        function (value, context) {
          const startMonth = context.parent.startMonth;
          const endMonth = value;

          return setMonthToNumber(endMonth) >= setMonthToNumber(startMonth);
        }
      ),
    otherwise: yup.string().nullable(),
  }),
  sector: yup.string().required("Sector is required"),
  skill: yup.array().required("Skill is required"),

  customerName: yup.string().required("Customer name ended is required"),
  roleInProject: yup.array(),
  description: yup.string(),
  /*  projectUrl: yup.string().matches(projRegex, "Enter a valid link to project"), */
  /* keyAssignments: yup.boolean(), */
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Projects = ({ projs, cvId, label }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState();
  const [projectsState, setProjectsState] = useState(projs);
  const [projectElement, setProjectElement] = useState(null);
  const [logStartYear, setLogStartYear] = useState();
  const [logEndYear, setLogEndYear] = useState();
  const [selectedRole, setSelectedRole] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState([]);
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };
  const handleClose = () => {
    reset(); // Reset the form fields

    setSelectedRole([]);
    setSelectedSkill([]);
    setLogStartYear();
    setLogEndYear();
    setValue("onGoing", false);
    setOpen(false);
  };

  //disable key assignments checkbox after two projects
  /* const isCheckboxDisabled =
    projs.filter((proj) => proj.keyAssignments === true).length >= 2; */

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { values, errors },
  } = useForm({
    resolver: yupResolver(projectSchema),
    defaultValues: {
      name: "",
      onGoing: false, // Add a default value for the onGoing field
      startMonth: "",
      startYear: "",
      endMonth: "",
      endYear: "",
      sector: "",
      skill: [],
      customerName: "",
      roleInProject: [],
      description: "",
      /* projectUrl: "", */
      /* keyAssignments: false, */
      excludeInCv: false,
    },
  });

  const [addNewProject] = useAddNewProjectMutation();
  const submitResults = async (e) => {
    const newProj = e;

    await addNewProject({ cvId, projs: [newProj] })
      .then((result) => {
        handleClose();
        projs.push(newProj);
        toast.success("Project added successfully");
      })
      .catch((error) => {
        console.log("Add new project error:", error);
      });
  };

  const [deleteCvProject] = useDeleteCvProjectMutation();
  const handleRemove = async (item) => {
    await deleteCvProject({ cvId, projId: item }).unwrap();
    const updatedCvProjects = projectsState.filter(
      (project) => project.id !== item
    );
    setProjectsState(updatedCvProjects);
    projs.splice(
      projs.findIndex((project) => project._id === item),
      1
    );

    toast.info("Project removed successfully");
  };

  const [editCvProject] = useEditCvProjectMutation();
  const handleEdit = async (projectId, mode) => {
    setOpen(true);
    setMode(mode);
    setProjectId(projectId);
  };

  useFormPersist("projects", {
    watch,
    setValue,
    storage: window.localStorage, // default window.sessionStorage
  });

  useEffect(() => {
    const data = localStorage.getItem("projects");
    if (data !== null) JSON.parse(data);
  }, []);

  useEffect(() => {
    localStorage.setItem("projects", JSON.stringify(projs));
  }, [projs]);

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    projs.forEach((proj) => {
      const savedState = localStorage.getItem(`excludeInCv-${proj._id}`);
      initialLocalExcludeInCv[proj._id] = savedState === "true";
    });

    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [projs]);

  const handleInputChange = (event, projectId, fieldName) => {
    const projectElement = projs.find((project) => project._id === projectId);
    projectElement[fieldName] = event.target.value;
    const updatedProjects = projs.map((project) => {
      if (project._id === projectId) {
        return { ...project, [fieldName]: event.target.value };
      }
      return project;
    });
    setProjectsState(updatedProjects);
  };

  const handleToggleChange = async (
    newExcludeInCv,
    projectId,
    fieldName,
    updatedValue
  ) => {
    try {
      const updatedProjects = projs.map((proj) => {
        if (proj._id === projectId) {
          return {
            ...proj,
            [fieldName]: updatedValue,
          };
        }
        return proj;
      });

      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [projectId]: newExcludeInCv,
      }));
      localStorage.setItem(`excludeInCv-${projectId}`, newExcludeInCv.toString());

      await editCvProject({
        cvId: cvId,
        projectId: projectId,
        projs: updatedProjects.map((proj) => ({
          ...proj,
          excludeInCv: newExcludeInCv,
        })),
      });

      setProjectsState(updatedProjects);
    } catch (error) {
      console.error("Error updating toggle:", error);
    }
  };

  const projectElmnt = projs.find((proj) => proj._id === projectId);
  const renderSingleElement = () => {
    return (
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ marginTop: "10px" }}
      >
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Project Name"
                fullWidth
                autoFocus
                error={!!errors.name}
                helperText={errors?.name ? errors.name?.message : ""}
                value={projectElmnt?.name}
                onChange={(e) => handleInputChange(e, projectId, "name")}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="startYear"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Start year"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.startYear}
                helperText={errors?.startYear ? errors.startYear?.message : ""}
                value={projectElmnt?.startYear}
                onChange={(e) => handleInputChange(e, projectId, "startYear")}
              >
                {years.map((year) => (
                  <MenuItem value={year} key={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="startMonth"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Start month"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.startMonth}
                helperText={
                  errors?.startMonth ? errors.startMonth?.message : ""
                }
                value={projectElmnt?.startMonth}
                onChange={(e) => handleInputChange(e, projectId, "startMonth")}
              >
                {monthNames.map((month) => (
                  <MenuItem value={month} key={month}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="endMonth"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="End month"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.endMonth}
                helperText={errors?.endMonth ? errors.endMonth?.message : ""}
                value={projectElmnt?.endMonth}
                onChange={(e) => handleInputChange(e, projectId, "endMonth")}
              >
                {monthNames.map((month) => (
                  <MenuItem value={month} key={month}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="endYear"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="End year"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.endYear}
                helperText={errors?.endYear ? errors.endYear?.message : ""}
                value={projectElmnt?.endYear}
                onChange={(e) => handleInputChange(e, projectId, "endYear")}
              >
                {years.map((year) => (
                  <MenuItem value={year} key={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="sector"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Sector"
                fullWidth
                error={!!errors.sector}
                helperText={errors?.sector ? errors.sector?.message : ""}
                value={projectElmnt?.sector}
                onChange={(e) => handleInputChange(e, projectId, "sector")}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="customerName"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Customer Name"
                fullWidth
                error={!!errors.customerName}
                helperText={
                  errors?.customerName ? errors.customerName?.message : ""
                }
                value={projectElmnt?.customerName}
                onChange={(e) =>
                  handleInputChange(e, projectId, "customerName")
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                error={!!errors.description}
                helperText={
                  errors?.description ? errors.description?.message : ""
                }
                value={projectElmnt?.description}
                onChange={(e) => handleInputChange(e, projectId, "description")}
              />
            )}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <Controller
            name="projectUrl"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Url to Project"
                fullWidth
                error={!!errors.projectUrl}
                helperText={
                  errors?.projectUrl ? errors.projectUrl?.message : ""
                }
                value={projectElmnt.projectUrl}
                onChange={(e) => handleInputChange(e, projectId, "projectUrl")}
              />
            )}
          />
        </Grid> */}
      </Grid>
    );
  };

  const submitEditResults = async () => {
    await editCvProject({
      cvId,
      projectId,
      projs: [projectElmnt],
    }).unwrap();
    handleClose();
    toast.info("Projects updated successfully");
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = [];
  for (let year = 2023; year >= 1940; year--) {
    years.push(year);
  }
  console.log(projs, "this is the project");

  return (
    <div id="Projects">
      <ToastContainer autoClose={1500} />
      <Box display="flex" flexDirection="column" gap={1} sx={{ paddingLeft: 5 }}>
        <Typography variant="h4">Projects</Typography>
        <Typography variant="subtitle1">Key Contributions & Initiatives</Typography>
      </Box>
      <Grid container xs={10} sx={{ paddingLeft: 5 }}>
        {projs.length === 0 ? (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{ marginTop: "10px" }}
            >
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      label="Project Name"
                      fullWidth
                      autoFocus
                      error={!!errors.name}
                      helperText={errors?.name ? errors.name?.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="startYear"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      label="Start year"
                      fullWidth
                      SelectProps={{
                        MenuProps: {
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "center",
                          },
                        },
                      }}
                      error={!!errors.startYear}
                      helperText={
                        errors?.startYear ? errors.startYear?.message : ""
                      }
                      onChange={(e) => {
                        setLogStartYear(e.target.value);
                        field.onChange(e);
                      }}
                    >
                      {years.map((year) => (
                        <MenuItem value={year} key={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="startMonth"
                  control={control}

                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      label="Start month"
                      fullWidth
                      SelectProps={{
                        MenuProps: {
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "center",
                          },
                        },
                      }}
                      error={!!errors.startMonth}
                      helperText={
                        errors?.startMonth ? errors.startMonth?.message : ""
                      }
                    >
                      {monthNames.map((month) => (
                        <MenuItem value={month} key={month}>
                          {month}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl>
                  <FormHelperText error={!!errors.onGoing}>
                    {errors?.onGoing ? errors.onGoing?.message : ""}
                  </FormHelperText>
                  <Controller
                    name="onGoing"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <FormControlLabel
                        label="On Going"
                        sx={{ margin: "0px" }}
                        control={
                          <Checkbox
                            onChange={onChange}
                            checked={value}
                            {...field}
                            ref={null}
                          />
                        }
                      >
                        {!value &&
                          watch("onGoing", () => {
                            console.log("onGoing checkbox is unchecked");
                            setValue("endYear", "");
                            setValue("endMonth", "");
                          })}
                      </FormControlLabel>
                    )}
                  />
                </FormControl>
              </Grid>
              {!watch("onGoing") && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name="endYear"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => {
                        const filteredYears = years.filter(
                          (year) => year >= logStartYear
                        );
                        return (
                          <TextField
                            {...field}
                            select
                            label="End year"
                            fullWidth
                            InputLabelProps={{
                              shrink: true, // forces label to always float at the top
                            }}
                            SelectProps={{
                              MenuProps: {
                                anchorOrigin: {
                                  vertical: "bottom",
                                  horizontal: "center",
                                },
                              },
                            }}
                            logEndYear={logEndYear}
                            error={!!errors.endYear}
                            helperText={
                              errors?.endYear ? errors.endYear?.message : ""
                            }
                            value={values?.endYear} // Update value prop
                            onChange={(e) => {
                              setLogEndYear(e.target.value);
                              field.onChange(e);
                            }}
                          >
                            {filteredYears.map((year) => (
                              <MenuItem value={year} key={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="endMonth"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          InputLabelProps={{
                            shrink: true, // forces label to always float at the top
                          }}
                          label="End month"
                          fullWidth
                          SelectProps={{
                            MenuProps: {
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "center",
                              },
                            },
                          }}
                          error={!!errors.endMonth}
                          helperText={
                            errors?.endMonth ? errors.endMonth?.message : ""
                          }
                        >
                          {monthNames.map((month) => (
                            <MenuItem value={month} key={month}>
                              {month}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Controller
                  name="sector"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sector"
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      fullWidth
                      error={!!errors.sector}
                      helperText={
                        errors?.sector ? errors.sector?.message : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="Skill"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl fullWidth variant="outlined">
                      <Select
                        {...field}
                        error={!!errors.skill}
                        options={skillsUsedInProjects}
                        placeholder="Start typing skills used in project"
                        noOptionsMessage={() =>
                          "The mentioned skill isn't available"
                        }
                        isClearable
                        isMulti
                        isSearchable
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            const selectedValues = selectedOption.map(
                              (options) => options.value
                            );
                            setSelectedSkill(selectedValues);
                            setValue("skill", selectedValues); // update form value
                          } else {
                            setSelectedSkill([]);
                            setValue("skill", []); // update form value
                          }
                        }}
                        value={skillsUsedInProjects.filter((options) =>
                          selectedSkill.includes(options.value)
                        )}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "55px",
                            borderColor: state.isFocused ? "#E84610" : base.borderColor,
                            boxShadow: "none", // remove default blue shadow
                            "&:hover": {
                              borderColor: "#E84610",
                              border: "1px solid #E84610"
                            },
                          }),
                          input: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller input text
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller selected text
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller dropdown option text
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller text for multi-selected chips
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontSize: "14px",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 100,
                          }),
                        }}
                      />
                      {errors?.skill && (
                        <FormHelperText error>
                          {errors.skill.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="customerName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Customer Name"
                      fullWidth
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      error={!!errors.customerName}
                      helperText={
                        errors?.customerName
                          ? errors.customerName?.message
                          : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="roleInProject"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange } }) => (
                    <Select
                      error={!!errors.roleInProject}
                      options={roleInProject}
                      placeholder="Start typing your role in the project"
                      noOptionsMessage={() =>
                        "The mentioned role isn't available"
                      }
                      isClearable
                      isMulti
                      isSearchable
                      fullWidth
                      helperText={
                        errors?.roleInProject
                          ? errors.roleInProject?.message
                          : ""
                      }
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const selectedValues = selectedOption.map(
                            (option) => option.value
                          );
                          setSelectedRole(selectedValues);
                          setValue("roleInProject", selectedValues); // update form value
                        } else {
                          setSelectedRole([]);
                          setValue("roleInProject", []); // update form value
                        }
                      }}
                      value={roleInProject.filter((option) =>
                        selectedRole.includes(option.value)
                      )}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "55px",
                          borderColor: state.isFocused ? "#E84610" : base.borderColor,
                          boxShadow: "none", // remove default blue shadow
                          "&:hover": {
                            borderColor: "#E84610",
                            border: "1px solid #E84610"
                          },
                        }),
                        input: (base) => ({
                          ...base,
                          fontSize: "14px", // smaller input text
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "14px", // smaller selected text
                        }),
                        option: (base) => ({
                          ...base,
                          fontSize: "14px", // smaller dropdown option text
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          fontSize: "14px", // smaller text for multi-selected chips
                        }),
                        placeholder: (base) => ({
                          ...base,
                          fontSize: "14px",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 100,
                        }),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      InputLabelProps={{
                        shrink: true, // forces label to always float at the top
                      }}
                      fullWidth
                      error={!!errors.description}
                      helperText={
                        errors?.description ? errors.description?.message : ""
                      }
                    />
                  )}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <Controller
                  name="projectUrl"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Url to Project"
                      fullWidth
                      error={!!errors.projectUrl}
                      helperText={
                        errors?.projectUrl ? errors.projectUrl?.message : ""
                      }
                    />
                  )}
                />
              </Grid> */}

              {/* <Grid item xs={12}>
                <FormControl>
                  <FormHelperText error={!!errors.keyAssignments}>
                    {errors?.keyAssignments
                      ? errors.keyAssignments?.message
                      : ""}
                  </FormHelperText>
                  <Controller
                    name="keyAssignments"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Tooltip title="You can only add up to two key assignments">
                        <FormControlLabel
                          label="Key Assignment"
                          sx={{ margin: "0px" }}
                          control={
                            <Checkbox
                              onChange={onChange}
                              checked={value}
                              {...field}
                              ref={null}
                              disabled={isCheckboxDisabled}
                            />
                          }
                        />
                      </Tooltip>
                    )}
                  />
                </FormControl>
              </Grid> */}
              <Grid item xs={12} sx={{ marginTop: "1rem" }}>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    width: 82, // updated width
                    height: 42, // px
                    backgroundColor: "#463C3B",
                    borderRadius: "8px", // Corner/Small
                    padding: "8px 22px", // top/bottom 8px, left/right 22px
                    opacity: 1,
                    textTransform: "none", // keep text as-is
                    boxShadow: "none", // optional: remove default shadow
                  }}
                >
                  Save
                </Button>
              </Grid>
            </Grid>


            <Grid
              container
              sx={{ marginTop: "10px" }}
              justifyContent="flex-end"
            >
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)} // goes back one step in history
                    sx={{
                      width: 84,
                      height: 42,
                      borderRadius: "8px",
                      borderWidth: "1px",
                      borderColor: "currentColor",
                      padding: "8px 22px",
                      opacity: 1,
                      textTransform: "none",
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      width: 84, // px
                      height: 42, // px
                      borderRadius: "8px", // Corner/Small
                      padding: "8px 22px", // top/bottom 8px, left/right 22px
                      opacity: 1,
                      textTransform: "none", // keep text as-is
                      boxShadow: "none", // optional: remove default shadow
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>

              <ProjectDisplay
                initialBoxes={projs} // ✅ filtered data
                onBoxesChange={(updated) =>
                  console.log("Updated projects:", updated)
                }
                handleEdit={handleEdit}
                handleRemove={handleRemove}
              />

              <Grid item xs={12} sx={{ marginTop: "2rem" }}>
                <Button
                  onClick={() => handleOpen("add")}
                  disableRipple
                  disableFocusRipple
                  sx={{
                    color: "black",
                    textTransform: "uppercase",
                    textDecoration: "underline",
                    textDecorationStyle: "solid",
                    fontFamily: "fontFamily",
                    fontWeight: "fontWeightMedium",
                    fontSize: "0.9375rem",
                    lineHeight: "26px",
                    letterSpacing: "0.46px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px", // tight spacing between icon and text
                    padding: 0, // remove button padding
                    minWidth: "unset", // avoid default button minWidth
                  }}
                >
                  <AddIcon sx={{ fontSize: "1rem", color: "black" }} />
                  Add Another
                </Button>
              </Grid>

              <Grid
                container
                sx={{ marginTop: "30px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      variant="outlined" // outlined style
                      onClick={() => navigate(-1)} // goes back one step in history
                      sx={{
                        width: 84, // width in px
                        height: 42, // height in px
                        borderRadius: "8px", // "Corner/Small" -> approx 8px
                        borderWidth: "1px",
                        borderColor: "currentColor", // uses current text/icon color
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keeps text as-is
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        width: 84, // px
                        height: 42, // px
                        borderRadius: "8px", // Corner/Small
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keep text as-is
                        boxShadow: "none", // optional: remove default shadow
                      }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>


          </Grid>
        )}

      </Grid>

      {/* <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ marginTop: "20px", padding: "30px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ marginTop: "5px" }}
            >
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 0, pb: 0 }}
                    >
                      {label === "Key Assignments"
                        ? "Key Assignments"
                        : "Projects"}{" "}
                      &nbsp;
                      {label === "Projects" && (
                        <Tooltip
                          disableFocusListener
                          disableTouchListener
                          title="This section is mandatory"
                        >
                          <span style={{ color: "red" }}>*</span>
                        </Tooltip>
                      )}
                    </Typography>
                  </Grid>

                  {label !== "Key Assignments" && (
                    <>
                      <Grid item xs={1}>
                        <IconButton
                          aria-label="add"
                          onClick={() => handleOpen("add")}
                        >
                          <AddIcon color="primary" />
                        </IconButton>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpen("edit")}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {projs?.map((proj) => (
                  <List key={proj._id} sx={{ padding: "0px" }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: cyan[700] }}>
                          <AssignmentIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ paddingLeft: "10px" }}
                        primary={
                          <Typography variant="subtitle2" components="div">
                            Project : {proj.name}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              Start Date: {proj.startMonth} {proj.startYear}
                            </Typography>

                            {proj.endMonth && proj.endYear && (
                              <Typography variant="caption" display="block">
                                End Date: {proj.endMonth} {proj.endYear}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Customer Name : {proj.customerName}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Sector : {proj.sector}
                            </Typography>

                            {proj.skill && (
                              <Typography variant="caption" display="block">
                                Skill(s): {proj.skill.join(", ")}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Role : {proj.roleInProject.join(", ")}
                            </Typography>

                            {proj.onGoing && (
                              <Typography variant="caption" display="block">
                                Done : {proj.onGoing ? "No" : "Yes"}
                              </Typography>
                            )}

                            {proj.keyAssignments && (
                              <Typography variant="caption" display="block">
                                Key Assignment :{" "}
                                {proj.keyAssignments ? "Yes" : "No"}
                              </Typography>
                            )}

                            {proj.description && (
                              <Typography variant="caption" display="block">
                                Description: {proj.description}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Exclude in cv: {proj.excludeInCv ? "Yes" : "No"}
                            </Typography>
                          </>
                        }
                      />

                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{ textAlign: "right", paddingRight: "40px" }}
                      >
                        <FormControl>
                          <FormHelperText error={!!errors.excludeInCv}>
                            {errors?.excludeInCv
                              ? errors.excludeInCv?.message
                              : ""}
                          </FormHelperText>
                          <Controller
                            key={proj._id}
                            name={`excludeInCv-${proj._id}`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                            }) => (
                              <Tooltip title="This will exclude the section from the CV">
                                <FormControlLabel
                                  sx={{ margin: "0px" }}
                                  control={
                                    <Switch
                                      onChange={(e) => {
                                        const newExcludeInCv =
                                          e.target.checked;

                                        // Update the local state for the specific certificate immediately
                                        setLocalExcludeInCv(
                                          (prevLocalExcludeInCv) => ({
                                            ...prevLocalExcludeInCv,
                                            [proj._id]: newExcludeInCv,
                                          })
                                        );

                                        handleToggleChange(
                                          newExcludeInCv,
                                          proj._id,
                                          "excludeInCv"
                                        );

                                        console.log(
                                          "Toggle updated successfully"
                                        );
                                      }}
                                      checked={
                                        localExcludeInCv[proj._id] || false
                                      }
                                      {...field}
                                      ref={null}
                                    />
                                  }
                                />
                              </Tooltip>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid> */}

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >

        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>

            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="center" position="relative">

                {/* Centered title */}
                <Typography variant="h6" component="div">
                  Add a New Project
                </Typography>

                {/* Cancel icon on the left */}
                <IconButton
                  onClick={handleClose}
                  sx={{ position: "absolute", right: 8 }} // positions it to the left
                >
                  <CancelIcon />
                </IconButton>

              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ marginTop: "10px" }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Project Name"
                        fullWidth
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        autoFocus
                        error={!!errors.name}
                        helperText={errors?.name ? errors.name?.message : ""}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="startYear"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Start year"
                        fullWidth
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.startYear}
                        helperText={
                          errors?.startYear ? errors.startYear?.message : ""
                        }
                        onChange={(e) => {
                          setLogStartYear(e.target.value);
                          field.onChange(e);
                        }}
                      >
                        {years.map((year) => (
                          <MenuItem value={year} key={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="startMonth"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        label="Start month"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.startMonth}
                        helperText={
                          errors?.startMonth ? errors.startMonth?.message : ""
                        }
                      >
                        {monthNames.map((month) => (
                          <MenuItem value={month} key={month}>
                            {month}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl>
                    <FormHelperText error={!!errors.onGoing}>
                      {errors?.onGoing ? errors.onGoing?.message : ""}
                    </FormHelperText>
                    <Controller
                      name="onGoing"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <FormControlLabel
                          label="On Going"
                          sx={{ margin: "0px" }}
                          control={
                            <Checkbox
                              onChange={onChange}
                              checked={value}
                              {...field}
                              ref={null}
                            />
                          }
                        >
                          {!value &&
                            watch("onGoing", () => {
                              console.log("onGoing checkbox is unchecked");
                              setValue("endYear", "");
                              setValue("endMonth", "");
                            })}
                        </FormControlLabel>
                      )}
                    />
                  </FormControl>
                </Grid>
                {!watch("onGoing") && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="endYear"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => {
                          const filteredYears = years.filter(
                            (year) => year >= logStartYear
                          );
                          return (
                            <TextField
                              {...field}
                              select
                              InputLabelProps={{
                                shrink: true, // forces label to always float at the top
                              }}
                              label="End year"
                              fullWidth
                              SelectProps={{
                                MenuProps: {
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "center",
                                  },
                                },
                              }}
                              logEndYear={logEndYear}
                              error={!!errors.endYear}
                              helperText={
                                errors?.endYear ? errors.endYear?.message : ""
                              }
                              value={values?.endYear} // Update value prop
                              onChange={(e) => {
                                setLogEndYear(e.target.value);
                                field.onChange(e);
                              }}
                            >
                              {filteredYears.map((year) => (
                                <MenuItem value={year} key={year}>
                                  {year}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="endMonth"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            InputLabelProps={{
                              shrink: true, // forces label to always float at the top
                            }}
                            label="End month"
                            fullWidth
                            SelectProps={{
                              MenuProps: {
                                anchorOrigin: {
                                  vertical: "bottom",
                                  horizontal: "center",
                                },
                              },
                            }}
                            error={!!errors.endMonth}
                            helperText={
                              errors?.endMonth ? errors.endMonth?.message : ""
                            }
                          >
                            {monthNames.map((month) => (
                              <MenuItem value={month} key={month}>
                                {month}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Controller
                    name="sector"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Sector"
                        fullWidth
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        error={!!errors.sector}
                        helperText={
                          errors?.sector ? errors.sector?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="Skill"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl fullWidth variant="outlined">
                        <Select
                          {...field}
                          error={!!errors.skill}
                          options={skillsUsedInProjects}
                          placeholder="Start typing skills used in project"
                          noOptionsMessage={() =>
                            "The mentioned skill isn't available"
                          }
                          isClearable
                          isMulti
                          isSearchable
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              const selectedValues = selectedOption.map(
                                (options) => options.value
                              );
                              setSelectedSkill(selectedValues);
                              setValue("skill", selectedValues); // update form value
                            } else {
                              setSelectedSkill([]);
                              setValue("skill", []); // update form value
                            }
                          }}
                          value={skillsUsedInProjects.filter((options) =>
                            selectedSkill.includes(options.value)
                          )}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: "55px",
                              borderColor: state.isFocused ? "#E84610" : base.borderColor,
                              boxShadow: "none", // remove default blue shadow
                              "&:hover": {
                                borderColor: "#E84610",
                                border: "1px solid #E84610"
                              },
                            }),
                            input: (base) => ({
                              ...base,
                              fontSize: "14px", // smaller input text
                            }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: "14px", // smaller selected text
                            }),
                            option: (base) => ({
                              ...base,
                              fontSize: "14px", // smaller dropdown option text
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              fontSize: "14px", // smaller text for multi-selected chips
                            }),
                            placeholder: (base) => ({
                              ...base,
                              fontSize: "14px",
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 100,
                            }),
                          }}
                        />
                        {errors?.skill && (
                          <FormHelperText error>
                            {errors.skill.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="customerName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Customer Name"
                        fullWidth
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        error={!!errors.customerName}
                        helperText={
                          errors?.customerName
                            ? errors.customerName?.message
                            : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="roleInProject"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange } }) => (
                      <Select
                        error={!!errors.roleInProject}
                        options={roleInProject}
                        placeholder="Start typing your role in the project"
                        noOptionsMessage={() =>
                          "The mentioned role isn't available"
                        }
                        isClearable
                        isMulti
                        isSearchable
                        fullWidth
                        helperText={
                          errors?.roleInProject
                            ? errors.roleInProject?.message
                            : ""
                        }
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            const selectedValues = selectedOption.map(
                              (option) => option.value
                            );
                            setSelectedRole(selectedValues);
                            setValue("roleInProject", selectedValues); // update form value
                          } else {
                            setSelectedRole([]);
                            setValue("roleInProject", []); // update form value
                          }
                        }}
                        value={roleInProject.filter((option) =>
                          selectedRole.includes(option.value)
                        )}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "55px",
                            borderColor: state.isFocused ? "#E84610" : base.borderColor,
                            boxShadow: "none", // remove default blue shadow
                            "&:hover": {
                              borderColor: "#E84610",
                              border: "1px solid #E84610"
                            },
                          }),
                          input: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller input text
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller selected text
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller dropdown option text
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            fontSize: "14px", // smaller text for multi-selected chips
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontSize: "14px",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 100,
                          }),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        InputLabelProps={{
                          shrink: true, // forces label to always float at the top
                        }}
                        error={!!errors.description}
                        helperText={
                          errors?.description ? errors.description?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <Controller
                    name="projectUrl"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Url to Project"
                        fullWidth
                        error={!!errors.projectUrl}
                        helperText={
                          errors?.projectUrl ? errors.projectUrl?.message : ""
                        }
                      />
                    )}
                  />
                </Grid> */}

                {/* <Grid item xs={12}>
                  <FormControl>
                    <FormHelperText error={!!errors.keyAssignments}>
                      {errors?.keyAssignments
                        ? errors.keyAssignments?.message
                        : ""}
                    </FormHelperText>
                    <Controller
                      name="keyAssignments"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <Tooltip title="You can only add up to two key assignments">
                          <FormControlLabel
                            label="Key Assignment"
                            sx={{ margin: "0px" }}
                            control={
                              <Checkbox
                                onChange={onChange}
                                checked={value}
                                {...field}
                                ref={null}
                                disabled={isCheckboxDisabled}
                              />
                            }
                          />
                        </Tooltip>
                      )}
                    />
                  </FormControl>
                </Grid> */}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ marginTop: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" justifyContent="space-between">
                    <Button
                      variant="outlined" // outlined style
                      sx={{
                        width: 84, // width in px
                        height: 42, // height in px
                        borderRadius: "8px", // "Corner/Small" -> approx 8px
                        borderWidth: "1px",
                        borderColor: "currentColor", // uses current text/icon color
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keeps text as-is
                      }}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{
                        width: 84, // px
                        height: 42, // px
                        borderRadius: "8px", // Corner/Small
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keep text as-is
                        boxShadow: "none", // optional: remove default shadow
                      }}
                      variant="contained"
                      type="submit"
                    >
                      Add
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Certifications</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ marginTop: "5px" }}
              >
                <Grid item xs={12}>
                  {projs?.map((proj) => (
                    <List key={proj._id} sx={{ padding: "0px" }}>
                      <ListItem
                        sx={{ paddingLeft: "10px" }}
                        secondaryAction={
                          <Stack
                            alignItems="flex-end"
                            direction="row"
                            paddingBottom="15px"
                          >
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(proj._id, "editnew")}
                              sx={{ margin: "0px", padding: "0px" }}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(proj._id)}
                              sx={{ margin: "0px", padding: "0px" }}
                            >
                              <DeleteIcon sx={{ color: red[400] }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          sx={{ paddingRight: "20px", margin: "0px" }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Project: {proj.name}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" display="block">
                                Start Date: {proj.startMonth} {proj.startYear}
                              </Typography>
                              <Typography variant="caption" display="block">
                                End Date: {proj.endMonth} {proj.endYear}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Customer Name: {proj.customerName}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Sector: {proj.sector}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Skill(s): {proj.skill.join(", ")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Role: {proj.roleInProject.join(", ")}{" "}
                              </Typography>
                              Done: {proj.onGoing ? "No" : "Yes"}
                              {/* <Typography variant="caption" display="block">
                                Key Assignment:{" "}
                                {proj.keyAssignments ? "Yes" : "No"}
                              </Typography> */}
                              <Typography variant="caption" display="block">
                                Description: {proj.description}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Exclude in cv: {proj.excludeInCv ? "Yes" : "No"}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >

            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="center" position="relative">

                {/* Centered title */}
                <Typography variant="h6" component="div">
                  Edit Project
                </Typography>

                {/* Cancel icon on the left */}
                <IconButton
                  onClick={handleClose}
                  sx={{ position: "absolute", right: 8 }} // positions it to the left
                >
                  <CancelIcon />
                </IconButton>

              </Box>
            </DialogTitle>
            <DialogContent>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" justifyContent="space-between">
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default Projects;